import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HeaderV2 from '../components/HeaderV2';
import { lumi } from '../lib/lumi';
import PartnersHeroV2 from './partners/v2/PartnersHeroV2';
import PartnersGridV2 from './partners/v2/PartnersGridV2';
import { PartnerV2 } from './partners/v2/types';

const PartnersPageV2: React.FC = () => {
  const location = useLocation();
  const lang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh';
  const isZh = lang === 'zh';
  const isPreviewRoute = location.pathname.includes('/v2');

  const [partners, setPartners] = useState<PartnerV2[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        const result: any = await lumi.entities.partners.list({
          filter: { isPublic: true, status: 'active' },
          sort: { createdAt: -1 },
          limit: 100,
        });
        const list = Array.isArray(result) ? result : (result?.list || []);
        if (!cancelled) setPartners(list);
      } catch {
        if (!cancelled) setPartners([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = 'partners-v2-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500&family=Noto+Serif+TC:wght@400;500&display=swap';
    document.head.appendChild(link);
  }, []);

  const pageTitle = isZh ? '合作夥伴（V2 預覽）｜ONGU Studio' : 'Partners (V2 Preview) - ONGU Studio';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        {isPreviewRoute && <meta name="robots" content="noindex, nofollow" />}
        <link rel="canonical" href={`https://www.ongucoffee.com/${lang}/partners`} />
      </Helmet>

      <div style={{ background: '#FAF9F1', minHeight: '100vh' }}>
        <HeaderV2 />
        <PartnersHeroV2 isZh={isZh} />
        <section style={{ background: '#FAF9F1' }}>
          <div className="max-w-[920px] mx-auto px-6 lg:px-12 pt-24 pb-4 text-center">
            <p
              className="text-black leading-[1.45]"
              style={{ fontFamily: '"EB Garamond","Noto Serif TC",serif', fontWeight: 400, fontSize: 'clamp(22px, 2.4vw, 36px)' }}
            >
              {isZh
                ? '與 ONGU 一同沖煮的咖啡品牌、選品店與生活空間。'
                : 'Cafés, concept stores and lifestyle spaces brewing alongside ONGU.'}
            </p>
          </div>
        </section>
        <PartnersGridV2 partners={partners} loading={loading} isZh={isZh} />

        <section className="bg-black text-white">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-28 text-center">
            <h2
              className="mb-8 leading-[1.1]"
              style={{ fontFamily: '"EB Garamond","Noto Serif TC",serif', fontWeight: 400, fontSize: 'clamp(36px, 5vw, 76px)' }}
            >
              {isZh ? '想成為下一個 ONGU 合作店？' : 'Become an ONGU partner?'}
            </h2>
            <p className="text-base text-white/75 max-w-2xl mx-auto leading-[1.9] mb-12">
              {isZh
                ? '從 5kg 起接受試烘合作，提供豆款共製、開店輔導與訓練資源。'
                : 'Trial-roast partnerships from 5kg, with co-creation, opening support and training.'}
            </p>
            <Link
              to={`/${lang}/wholesale`}
              className="inline-block px-12 py-4 border border-white text-xs tracking-[0.3em] uppercase hover:bg-white hover:text-black transition-colors"
            >
              {isZh ? '前往批發合作' : 'Go to Wholesale'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default PartnersPageV2;
